import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Users, Plus, MessageCircle, Calendar } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

interface StudyGroup {
  id: string;
  name: string;
  description: string;
  members: string[];
  created_by: string;
  created_at: string;
  topic: string;
}

const DashboardStudyGroups = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState<StudyGroup[]>([]);
  const [newGroup, setNewGroup] = useState({ name: "", description: "", topic: "" });
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate("/auth");
      return;
    }
    loadGroups();
  };

  const loadGroups = () => {
    // Using localStorage for demo purposes
    const savedGroups = localStorage.getItem("study_groups");
    if (savedGroups) {
      setGroups(JSON.parse(savedGroups));
    }
    setLoading(false);
  };

  const saveGroups = (updatedGroups: StudyGroup[]) => {
    localStorage.setItem("study_groups", JSON.stringify(updatedGroups));
    setGroups(updatedGroups);
  };

  const createGroup = async () => {
    if (!newGroup.name.trim() || !newGroup.topic.trim()) {
      toast({ 
        title: "Missing information", 
        description: "Please provide a group name and topic",
        variant: "destructive" 
      });
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const group: StudyGroup = {
      id: crypto.randomUUID(),
      name: newGroup.name,
      description: newGroup.description,
      topic: newGroup.topic,
      members: [user.id],
      created_by: user.id,
      created_at: new Date().toISOString()
    };

    saveGroups([...groups, group]);
    setNewGroup({ name: "", description: "", topic: "" });
    setIsDialogOpen(false);
    toast({ title: "Study group created successfully!" });
  };

  const joinGroup = async (groupId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const updated = groups.map(g => {
      if (g.id === groupId && !g.members.includes(user.id)) {
        return { ...g, members: [...g.members, user.id] };
      }
      return g;
    });

    saveGroups(updated);
    toast({ title: "Joined study group!" });
  };

  const leaveGroup = async (groupId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const updated = groups.map(g => {
      if (g.id === groupId) {
        return { ...g, members: g.members.filter(m => m !== user.id) };
      }
      return g;
    });

    saveGroups(updated.filter(g => g.members.length > 0));
    toast({ title: "Left study group" });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20 p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Create Group
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Study Group</DialogTitle>
                <DialogDescription>
                  Start a new study group and invite others to join
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Group Name</label>
                  <Input
                    placeholder="e.g., Morning Accountability"
                    value={newGroup.name}
                    onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Topic/Focus</label>
                  <Input
                    placeholder="e.g., Marketing, Product Development"
                    value={newGroup.topic}
                    onChange={(e) => setNewGroup({ ...newGroup, topic: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Description</label>
                  <Textarea
                    placeholder="What will this group focus on?"
                    value={newGroup.description}
                    onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
                  />
                </div>
                <Button onClick={createGroup} className="w-full">
                  Create Group
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="space-y-2">
          <h1 className="text-4xl font-bold">Study Groups</h1>
          <p className="text-muted-foreground">Connect and learn with fellow entrepreneurs</p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {groups.map((group) => (
            <Card key={group.id} className="hover-scale">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <CardTitle>{group.name}</CardTitle>
                    <Badge variant="secondary">{group.topic}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">{group.members.length}</span>
                  </div>
                </div>
                {group.description && (
                  <CardDescription className="mt-2">{group.description}</CardDescription>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback>U</AvatarFallback>
                  </Avatar>
                  {group.members.length > 1 && (
                    <>
                      <Avatar className="h-8 w-8 -ml-4">
                        <AvatarFallback>U</AvatarFallback>
                      </Avatar>
                      {group.members.length > 2 && (
                        <span className="text-sm text-muted-foreground ml-2">
                          +{group.members.length - 2} more
                        </span>
                      )}
                    </>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => navigate("/dashboard/community")}
                  >
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Discuss
                  </Button>
                  {group.members.includes("current-user-id") ? (
                    <Button 
                      variant="destructive" 
                      onClick={() => leaveGroup(group.id)}
                    >
                      Leave
                    </Button>
                  ) : (
                    <Button onClick={() => joinGroup(group.id)}>
                      Join
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}

          {groups.length === 0 && (
            <Card className="md:col-span-2">
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <Users className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-2">No study groups yet</p>
                <p className="text-sm text-muted-foreground mb-4">
                  Create the first group and start collaborating!
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardStudyGroups;
